import { memo, useCallback } from 'react';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { useUpdateMeasurementPoint } from '~/hooks/useUpdateMeasurementPoint';
import {
    Measurement,
    MeasurementDraft,
    measurementDraftInFrame,
    measurementInFrame,
} from '~/domain/measurement/measurement';
import { measurementListState } from '~/state/atoms/measurementAtoms';
import {
    angleDegAtVertex,
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import {
    formatAngleDeg,
    formatDistanceCm,
    formatMeasurementValue,
} from '~/utils/measurementFormat';
import { paneImageOf } from '~/engine/view/paneImage';
import { CanvasPoint, viewShowsPlane } from '~/engine/view/planeViewGeometry';
import {
    AnnotationPointDrag,
    ontoShownPane,
    useAnnotationPointDrag,
    usePlacedAnnotation,
} from '~/hooks/viewer/annotationOverlay';
import { SIZE_FAMILY_COLOR } from '~/utils/annotationKinds';
import {
    CanvasProjection,
    canvasProjection,
    DASHED,
    distanceLabelPos,
    HIT_BAND,
    PlacedAnnotationProps,
    placedShapeStyle,
    PlacedShape,
    SHAPE_STROKE_PX,
    unitTo,
    ValueLabel,
} from '../AnnotationOverlaySvg';

/** Radius of the small dot used for an angle vertex or a lone first point. */
const DOT_RADIUS_PX = 2.5;
/** Half-length of a caliper end-cap tick (perpendicular to the line). */
const CAP_HALF_PX = 6;
const ANGLE_ARC_RADIUS_PX = 22;

/**
 * SVG path for the interior angle arc at `vertex`, sweeping the short way
 * between the two arms. Returns null for a degenerate configuration.
 */
function angleArcPath(
    vertex: CanvasPoint,
    armA: CanvasPoint,
    armB: CanvasPoint
): string | null {
    const dirA = unitTo(vertex, armA);
    const dirB = unitTo(vertex, armB);
    if (!dirA || !dirB) {
        return null;
    }
    const a0 = Math.atan2(dirA.y, dirA.x);
    const a1 = Math.atan2(dirB.y, dirB.x);
    // Signed shortest delta in (-π, π], so the arc always spans the
    // interior (≤ 180°) angle.
    let delta = a1 - a0;
    while (delta <= -Math.PI) {
        delta += 2 * Math.PI;
    }
    while (delta > Math.PI) {
        delta -= 2 * Math.PI;
    }
    const start = {
        x: vertex.x + ANGLE_ARC_RADIUS_PX * Math.cos(a0),
        y: vertex.y + ANGLE_ARC_RADIUS_PX * Math.sin(a0),
    };
    const end = {
        x: vertex.x + ANGLE_ARC_RADIUS_PX * Math.cos(a0 + delta),
        y: vertex.y + ANGLE_ARC_RADIUS_PX * Math.sin(a0 + delta),
    };
    const sweepFlag = delta >= 0 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${ANGLE_ARC_RADIUS_PX} ${ANGLE_ARC_RADIUS_PX} 0 0 ${sweepFlag} ${end.x} ${end.y}`;
}

interface SegmentProps {
    a: CanvasPoint;
    b: CanvasPoint;
    color: string;
    strokeWidth?: number;
    strokeDasharray?: string;
}

const Segment = ({
    a,
    b,
    color,
    strokeWidth = SHAPE_STROKE_PX,
    strokeDasharray,
}: SegmentProps) => (
    <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
    />
);

/** Small filled dot — used for an angle vertex or a lone first point. */
const Dot = ({ at, color }: { at: CanvasPoint; color: string }) => (
    <circle
        cx={at.x}
        cy={at.y}
        r={DOT_RADIUS_PX}
        fill={color}
        stroke="#000"
        strokeWidth={0.75}
    />
);

/**
 * A caliper-style end-cap: a short tick perpendicular to the measured line,
 * the way a ruler or caliper terminates. Reads as an instrument (and is
 * easier to land an endpoint on) rather than a generic dot. Falls back to a
 * {@link Dot} when the line direction is unknown (a lone first point).
 */
const Tick = ({
    at,
    dir,
    color,
}: {
    at: CanvasPoint;
    dir: CanvasPoint | null;
    color: string;
}) => {
    if (!dir) {
        return <Dot at={at} color={color} />;
    }
    // Perpendicular to the line direction (-dy, dx), scaled to the cap length.
    const px = -dir.y * CAP_HALF_PX;
    const py = dir.x * CAP_HALF_PX;
    return (
        <line
            x1={at.x - px}
            y1={at.y - py}
            x2={at.x + px}
            y2={at.y + py}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
        />
    );
};

/** Label position for an angle: along the interior bisector from the vertex. */
function angleLabelPos(
    vertex: CanvasPoint,
    armA: CanvasPoint,
    armB: CanvasPoint
): CanvasPoint {
    const dirA = unitTo(vertex, armA);
    const dirB = unitTo(vertex, armB);
    if (!dirA || !dirB) {
        return { x: vertex.x + 10, y: vertex.y - 10 };
    }
    let bx = dirA.x + dirB.x;
    let by = dirA.y + dirB.y;
    const length = Math.hypot(bx, by);
    if (length < 1e-6) {
        // Arms are opposite (≈180°); offset perpendicular instead.
        return { x: vertex.x - dirA.y * 18, y: vertex.y + dirA.x * 18 };
    }
    bx /= length;
    by /= length;
    const offset = ANGLE_ARC_RADIUS_PX + 12;
    return { x: vertex.x + bx * offset, y: vertex.y + by * offset };
}

type MeasurementStyle = Partial<ReturnType<typeof placedShapeStyle>>;

function renderDistance(
    points: readonly CanvasPoint[],
    label: string | null,
    color: string,
    keyPrefix: string,
    style: MeasurementStyle = {}
) {
    const [a, b] = points;
    const dir = unitTo(a, b);
    const labelPos = distanceLabelPos(a, b);
    return (
        <g key={keyPrefix} opacity={style.opacity ?? 1} style={style.style}>
            <Segment a={a} b={b} color={color} {...style} />
            <Tick at={a} dir={dir} color={color} />
            <Tick at={b} dir={dir} color={color} />
            {label && <ValueLabel at={labelPos} text={label} color={color} />}
        </g>
    );
}

function renderAngle(
    points: readonly CanvasPoint[],
    label: string | null,
    color: string,
    keyPrefix: string,
    style: MeasurementStyle = {}
) {
    const [armA, vertex, armB] = points;
    const arc = angleArcPath(vertex, armA, armB);
    const labelPos = angleLabelPos(vertex, armA, armB);
    return (
        <g key={keyPrefix} opacity={style.opacity ?? 1} style={style.style}>
            <Segment a={armA} b={vertex} color={color} {...style} />
            <Segment a={vertex} b={armB} color={color} {...style} />
            {arc && (
                <path
                    d={arc}
                    fill="none"
                    stroke={color}
                    strokeWidth={1.5}
                    strokeDasharray={style.strokeDasharray}
                />
            )}
            <Tick at={armA} dir={unitTo(armA, vertex)} color={color} />
            <Dot at={vertex} color={color} />
            <Tick at={armB} dir={unitTo(armB, vertex)} color={color} />
            {label && <ValueLabel at={labelPos} text={label} color={color} />}
        </g>
    );
}

/** Render the in-progress placement: placed points + dashed rubber-band. */
export function renderDraft(
    view: PlaneView,
    { toCanvas }: CanvasProjection,
    stored: MeasurementDraft | null
) {
    const draft =
        stored && ontoShownPane(view, stored, measurementDraftInFrame);
    if (!draft) {
        return null;
    }
    const placed = draft.points;
    const hover = draft.hoverPointMm;

    if (draft.kind === 'distance') {
        if (placed.length === 1 && hover) {
            const a = toCanvas(placed[0]);
            const b = toCanvas(hover);
            return renderDistance(
                [a, b],
                formatDistanceCm(euclideanDistanceMm(placed[0], hover)),
                SIZE_FAMILY_COLOR,
                'draft',
                { strokeDasharray: DASHED }
            );
        }
        // Only the first point placed, no hover yet.
        return (
            <Dot
                key="draft"
                at={toCanvas(placed[0])}
                color={SIZE_FAMILY_COLOR}
            />
        );
    }

    // Angle draft.
    const canvasPlaced = placed.map(toCanvas);
    if (placed.length === 1) {
        const a = canvasPlaced[0];
        return (
            <g key="draft">
                {hover && (
                    <Segment
                        a={a}
                        b={toCanvas(hover)}
                        color={SIZE_FAMILY_COLOR}
                        strokeDasharray={DASHED}
                    />
                )}
                <Dot at={a} color={SIZE_FAMILY_COLOR} />
            </g>
        );
    }
    // Two points placed (armA, vertex); preview the second arm to the hover
    // point — with a live angle readout — once the cursor is in the viewport.
    const [armA, vertex] = canvasPlaced;
    const hoverCanvas = hover ? toCanvas(hover) : null;
    const liveAngle = hover
        ? angleDegAtVertex(placed[0], placed[1], hover)
        : NaN;
    return (
        <g key="draft">
            <Segment
                a={armA}
                b={vertex}
                color={SIZE_FAMILY_COLOR}
                strokeDasharray={DASHED}
            />
            {hoverCanvas && (
                <Segment
                    a={vertex}
                    b={hoverCanvas}
                    color={SIZE_FAMILY_COLOR}
                    strokeDasharray={DASHED}
                />
            )}
            {hoverCanvas && Number.isFinite(liveAngle) && (
                <ValueLabel
                    at={angleLabelPos(vertex, armA, hoverCanvas)}
                    text={formatAngleDeg(liveAngle)}
                    color={SIZE_FAMILY_COLOR}
                />
            )}
            <Tick
                at={armA}
                dir={unitTo(armA, vertex)}
                color={SIZE_FAMILY_COLOR}
            />
            <Dot at={vertex} color={SIZE_FAMILY_COLOR} />
        </g>
    );
}

interface HandleDrag {
    readonly id: string;
    readonly pointIndex: number;
    /** The plane of the measurement, as stored. */
    readonly plane: Measurement['plane'];
    /**
     * The dragged point where the drag started, on the pane. The moved point
     * stays on the plane through it along the view's current normal, from
     * whichever side the view shows it, unlike the stored slice position.
     */
    readonly anchorMm: PointMm;
}

/**
 * Drag interaction for editing a committed measurement's point: the moved
 * point is pinned back onto the measurement's own slice, keeping all of its
 * points coplanar, and stored in the frame of the image the pane shows
 * before the record is recomputed. A move is refused once the view no longer
 * shows the measurement's image: the point would be stored in another
 * image's frame.
 */
function useHandleDrag(view: PlaneView): AnnotationPointDrag<HandleDrag> {
    const updatePoint = useUpdateMeasurementPoint();

    const movePoint = useCallback(
        (drag: HandleDrag, pointMm: PointMm, dragView: PlaneView) => {
            const pane = paneImageOf(dragView);
            if (pane.kind === 'none' || !viewShowsPlane(dragView, drag.plane)) {
                return;
            }
            updatePoint(drag.id, drag.pointIndex, pane.toImageFrame(pointMm));
        },
        [updatePoint]
    );

    return useAnnotationPointDrag(view, movePoint);
}

const pointsOfMeasurement = (measurement: Measurement) => measurement.points;

/** Draws one placed distance or angle on a tile, with its value as its label. */
function MeasurementOverlay(props: PlacedAnnotationProps) {
    const { id, view, frame } = props;
    const { beginDrag, dragging } = useHandleDrag(view);
    const placed = usePlacedAnnotation(
        props,
        measurementListState,
        measurementInFrame,
        pointsOfMeasurement,
        dragging !== null
    );
    if (!placed) {
        return null;
    }
    const { stored, onPane: measurement } = placed;
    const points = measurement.points.map(
        canvasProjection(view, frame).toCanvas
    );
    const distance = measurement.kind === 'distance';
    return (
        <PlacedShape
            {...props}
            placed={placed}
            mode={measurement.kind}
            label={formatMeasurementValue(measurement)}
            labelAt={
                distance
                    ? distanceLabelPos(points[0], points[1])
                    : angleLabelPos(points[1], points[0], points[2])
            }
            bands={
                <polyline
                    points={points.map(({ x, y }) => `${x},${y}`).join(' ')}
                    {...HIT_BAND}
                />
            }
            handles={points}
            beginDrag={(event, pointIndex) =>
                beginDrag(event, {
                    id,
                    pointIndex,
                    plane: stored.plane,
                    anchorMm: measurement.points[pointIndex],
                })
            }
        >
            {(distance ? renderDistance : renderAngle)(
                points,
                null,
                SIZE_FAMILY_COLOR,
                id,
                placedShapeStyle(placed)
            )}
        </PlacedShape>
    );
}

export default memo(MeasurementOverlay);
